/**
 * Mobile navigation drawer with hamburger menu for landing page section navigation.
 *
 * What: Renders a slide-out drawer with section links, CTAs, and quick actions optimized for mobile.
 * Why: Provides intuitive mobile navigation to key sections and conversion points without cluttering the viewport.
 * When: Update when adding/removing landing sections, changing navigation structure, modifying CTA
 * behavior, or changing what `hideTrialCtas` suppresses for sales-free pages (e.g. partnership pages).
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  ArticleOutlined as BlogIcon,
  AutoAwesome as ApproachIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Groups as WhoTeachesIcon,
  HomeRounded as HomeIcon,
  InfoOutlined as AboutUsIcon,
  Login as LoginIcon,
  Menu as MenuIcon,
  MenuBook as SubjectsIcon,
  PsychologyAlt as HowItWorksIcon,
  ReportProblemOutlined as ProblemIcon,
  School as ExperienceIcon,
  TrendingUp as ResultsIcon,
  TrendingUp as TrackingIcon,
  VolunteerActivismOutlined as WhyIcon,
  WorkspacePremium as PricingIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';

import {
  landingNavConfig,
  landingNavItemTypes,
} from '@/components/landing/landingContent';
import { colors, radii } from '@/components/landing/landingTokens';
import ProUpsellDialog from '@/components/Practice/ProUpsellDialog';
import { useTuitionSubject } from '@/contexts/tuitionSubjectContext';
import { useWhatsAppClick } from '@/hooks/useWhatsAppClick';
import { navigateToLandingSection } from '@/utils/landingSectionNavigation';

// ---------------------------------------------------------------------------
// Drawer close transition duration (ms). The section-click scroll delay must
// exceed this so the body overflow lock is released before we scroll.
// ---------------------------------------------------------------------------
const DRAWER_TRANSITION_MS = 300;
const SCROLL_DELAY_MS = DRAWER_TRANSITION_MS + 50;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LandingMobileNavProps {
  onLogin: () => void;
  brandName?: string;
  activeSection?: string;
  /** When provided, the drawer is controlled externally. */
  open?: boolean;
  onClose?: () => void;
  /**
   * Hide every sales affordance (the "Book Free Trial" block, the "Chat on
   * WhatsApp" quick action and the lead-capture dialog). For pages that are
   * social good rather than acquisition, such as the partnership welcome pages.
   * Nav items and Login stay.
   */
  hideTrialCtas?: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function WhatsAppIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

const ICONS_BY_KEY: Record<string, SvgIconComponent> = {
  aboutUs: AboutUsIcon,
  blog: BlogIcon,
  home: HomeIcon,
  problem: ProblemIcon,
  approach: ApproachIcon,
  howItWorks: HowItWorksIcon,
  students: ExperienceIcon,
  parents: TrackingIcon,
  results: ResultsIcon,
  pricing: PricingIcon,
  why: WhyIcon,
  subjects: SubjectsIcon,
  whoTeaches: WhoTeachesIcon,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function LandingMobileNav({
  onLogin,
  brandName = 'DeepThink',
  activeSection = '',
  open: controlledOpen,
  onClose: controlledOnClose,
  hideTrialCtas = false,
}: LandingMobileNavProps) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const drawerOpen = isControlled ? controlledOpen : internalOpen;
  const [dialogOpen, setDialogOpen] = useState(false);
  const tuition = useTuitionSubject();
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const drawerId = 'landing-mobile-navigation-drawer';
  const drawerTitleId = 'landing-mobile-navigation-title';
  const subjectsToggleId = 'landing-mobile-subjects-toggle';
  const subjectsPanelId = 'landing-mobile-subjects-panel';
  const handleWhatsAppClick = useWhatsAppClick('landing_mobile_nav');

  const handleDrawerOpen = () => {
    setSubjectsOpen(false);
    if (!isControlled) setInternalOpen(true);
  };

  const handleDrawerClose = () => {
    if (isControlled && typeof controlledOnClose === 'function') {
      controlledOnClose();
    } else {
      setInternalOpen(false);
    }
  };

  const handleSectionClick = (sectionId: string) => {
    handleDrawerClose();
    // Delay navigation until after the Drawer close transition completes.
    // MUI's Drawer/Modal sets overflow:hidden on <body> while open, which blocks
    // programmatic scrolling. When already on the landing page the scroll fires
    // immediately and gets swallowed; the timeout lets the body unlock first.
    setTimeout(() => {
      navigateToLandingSection({
        sectionId,
        pathname,
        navigate,
        isMobile: true,
      });
    }, SCROLL_DELAY_MS);
  };

  const handleBookTrialClick = () => {
    handleDrawerClose();
    setDialogOpen(true);
  };

  const handleWhatsAppClickWithClose = () => {
    handleDrawerClose();
    handleWhatsAppClick();
  };

  const handleLoginClick = () => {
    handleDrawerClose();
    onLogin();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSubjectsToggle = () => {
    setSubjectsOpen(prev => !prev);
  };

  const isRouteNavItemActive = (routePath: string) =>
    pathname === routePath || pathname.startsWith(`${routePath}/`);

  return (
    <>
      {/* Hamburger Menu Button: only when uncontrolled (in-bar button used when controlled) */}
      {!isControlled && !drawerOpen && (
        <IconButton
          onClick={handleDrawerOpen}
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            position: 'fixed',
            top: 'calc(env(safe-area-inset-top) + 16px)',
            right: 'calc(16px + env(safe-area-inset-right))',
            zIndex: theme.zIndex.modal + 1,
            backgroundColor: colors.paper,
            border: `1px solid ${colors.border}`,
            borderRadius: radii.md,
            width: 48,
            height: 48,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            outline: '2px solid transparent',
            outlineOffset: 2,
            '&:focus-visible': {
              outline: `3px solid ${colors.accentCobalt}`,
              outlineOffset: 2,
            },
            '&:hover': {
              backgroundColor: colors.backgroundAlt,
            },
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          aria-label="Open navigation menu"
          aria-haspopup="dialog"
          aria-controls={drawerId}
          aria-expanded={drawerOpen}
        >
          <MenuIcon sx={{ color: colors.ink, fontSize: 24 }} />
        </IconButton>
      )}

      {/* Navigation Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          id: drawerId,
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': drawerTitleId,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: theme.zIndex.modal + 2,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
          },
          '& .MuiDrawer-paper': {
            width: { xs: '85vw', sm: 360 },
            maxWidth: 360,
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: colors.paper,
            backgroundImage: 'none',
            pt: 'calc(env(safe-area-inset-top) + 16px)',
            pb: 'calc(env(safe-area-inset-bottom) + 16px)',
            pl: 'env(safe-area-inset-left)',
            pr: 'env(safe-area-inset-right)',
            overscrollBehaviorY: 'contain',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
          },
        }}
        transitionDuration={DRAWER_TRANSITION_MS}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            pb: 2,
          }}
        >
          <Typography
            id={drawerTitleId}
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 800,
              color: colors.ink,
              fontSize: '1.125rem',
            }}
          >
            Menu
          </Typography>
          <IconButton
            onClick={handleDrawerClose}
            sx={{
              color: colors.inkSubtle,
              width: 48,
              height: 48,
              '&:hover': {
                backgroundColor: colors.backgroundAlt,
              },
            }}
            aria-label="Close navigation menu"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Primary CTA - Book Free Trial */}
        {!hideTrialCtas && (
          <Box sx={{ px: 3, pb: 3 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleBookTrialClick}
              startIcon={<CalendarIcon />}
              sx={{
                backgroundColor: colors.accentOrange,
                color: colors.paper,
                borderRadius: radii.md,
                py: 1.75,
                minHeight: 56,
                fontSize: '1rem',
                fontWeight: 800,
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(240, 104, 48, 0.25)',
                outline: '2px solid transparent',
                outlineOffset: 2,
                '&:focus-visible': {
                  outline: `3px solid ${colors.accentCobalt}`,
                  outlineOffset: 2,
                },
                '&:hover': {
                  backgroundColor: colors.accentOrangeLight,
                  boxShadow: '0 12px 32px rgba(240, 104, 48, 0.35)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {tuition.compactCta}
            </Button>
          </Box>
        )}

        <Divider sx={{ borderColor: colors.border, mb: 1 }} />

        {/* Section Navigation */}
        <Box component="nav" aria-label="Main mobile navigation">
          <List sx={{ px: 1.5 }}>
            {landingNavConfig.mobile.map((item, sectionIndex) => {
              if (
                item.type === landingNavItemTypes.dropdown &&
                'isDropdown' in item &&
                item.isDropdown
              ) {
                const Icon = ICONS_BY_KEY[item.iconKey];
                return (
                  <DropdownNavItem
                    key={`dropdown-${sectionIndex}`}
                    item={item}
                    Icon={Icon}
                    subjectsOpen={subjectsOpen}
                    subjectsToggleId={subjectsToggleId}
                    subjectsPanelId={subjectsPanelId}
                    onToggle={handleSubjectsToggle}
                    onClose={handleDrawerClose}
                  />
                );
              }

              if (
                item.type !== landingNavItemTypes.section &&
                item.type !== landingNavItemTypes.route
              ) {
                return null;
              }

              const isSectionItem = item.type === landingNavItemTypes.section;
              const Icon = ICONS_BY_KEY[item.iconKey];
              const isActive = isSectionItem
                ? activeSection === ('id' in item ? item.id : '')
                : isRouteNavItemActive('path' in item ? item.path : '');
              const navButtonProps = isSectionItem
                ? {
                    onClick: () =>
                      handleSectionClick('id' in item ? item.id : ''),
                    'aria-current': isActive
                      ? ('location' as const)
                      : undefined,
                  }
                : {
                    component: Link as React.ElementType,
                    to: 'path' in item ? item.path : '',
                    onClick: handleDrawerClose,
                    'aria-current': isActive ? ('page' as const) : undefined,
                  };

              return (
                <ListItem
                  key={
                    isSectionItem
                      ? 'id' in item
                        ? item.id
                        : sectionIndex
                      : 'path' in item
                        ? item.path
                        : sectionIndex
                  }
                  disablePadding
                  sx={{ mb: 0.5 }}
                >
                  <ListItemButton
                    {...navButtonProps}
                    sx={{
                      borderRadius: radii.md,
                      minHeight: 60,
                      px: 2.5,
                      backgroundColor: isActive
                        ? colors.backgroundAlt
                        : 'transparent',
                      outline: '2px solid transparent',
                      outlineOffset: 2,
                      '&:focus-visible': {
                        outline: `3px solid ${colors.accentCobalt}`,
                        outlineOffset: 2,
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 44,
                        color: isActive ? colors.accentOrange : colors.inkMuted,
                      }}
                    >
                      {Icon ? <Icon sx={{ fontSize: 24 }} /> : null}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '1.0625rem',
                        fontWeight: isActive ? 700 : 600,
                        color: isActive ? colors.ink : colors.inkMuted,
                        letterSpacing: -0.2,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Divider sx={{ borderColor: colors.border, my: 2 }} />

        {/* Quick Actions */}
        <Box component="nav" aria-label="Mobile quick actions">
          <List sx={{ px: 1.5 }}>
            {!hideTrialCtas && (
              <QuickActionItem
                icon={<WhatsAppIcon size={24} />}
                iconColor="#25D366"
                label="Chat on WhatsApp"
                onClick={handleWhatsAppClickWithClose}
              />
            )}
            <QuickActionItem
              icon={<LoginIcon />}
              iconColor={colors.inkMuted}
              label="Login"
              onClick={handleLoginClick}
            />
          </List>
        </Box>

        {/* Footer Info */}
        <Box sx={{ mt: 'auto', pt: 3, px: 3, pb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.inkSubtle,
              fontSize: '0.8125rem',
              lineHeight: 1.6,
              display: 'block',
            }}
          >
            {brandName} -{' '}
            {tuition.subject === 'mathematics'
              ? 'Primary & Secondary Math Tuition'
              : `Secondary ${tuition.label} Tuition`}
          </Typography>
        </Box>
      </Drawer>

      {/* Lead Capture Dialog */}
      {!hideTrialCtas && (
        <ProUpsellDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          skipOverview={true}
          sourceLocation="landing_mobile_nav"
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Extracted sub-components to reduce render method complexity
// ---------------------------------------------------------------------------

interface QuickActionItemProps {
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  onClick: () => void;
}

function QuickActionItem({
  icon,
  iconColor,
  label,
  onClick,
}: QuickActionItemProps) {
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        onClick={onClick}
        sx={{
          borderRadius: radii.sm,
          minHeight: 56,
          px: 2,
          outline: '2px solid transparent',
          outlineOffset: 2,
          '&:focus-visible': {
            outline: `3px solid ${colors.accentCobalt}`,
            outlineOffset: 2,
          },
          '&:hover': {
            backgroundColor: colors.backgroundAlt,
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: iconColor }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: '1rem',
            fontWeight: 500,
            color: colors.inkMuted,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

interface DropdownNavItemProps {
  item: (typeof landingNavConfig.mobile)[number];
  Icon: SvgIconComponent | undefined;
  subjectsOpen: boolean;
  subjectsToggleId: string;
  subjectsPanelId: string;
  onToggle: () => void;
  onClose: () => void;
}

function DropdownNavItem({
  item,
  Icon,
  subjectsOpen,
  subjectsToggleId,
  subjectsPanelId,
  onToggle,
  onClose,
}: DropdownNavItemProps) {
  const groups = 'groups' in item && item.groups ? item.groups : [];
  return (
    <Box>
      <ListItem disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          id={subjectsToggleId}
          onClick={onToggle}
          aria-expanded={subjectsOpen}
          aria-controls={subjectsPanelId}
          aria-haspopup="true"
          sx={{
            borderRadius: radii.md,
            minHeight: 60,
            px: 2.5,
            backgroundColor: subjectsOpen
              ? colors.backgroundAlt
              : 'transparent',
            outline: '2px solid transparent',
            outlineOffset: 2,
            '&:focus-visible': {
              outline: `3px solid ${colors.accentCobalt}`,
              outlineOffset: 2,
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 44,
              color: subjectsOpen ? colors.accentOrange : colors.inkMuted,
            }}
          >
            {Icon ? <Icon sx={{ fontSize: 24 }} /> : null}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '1.0625rem',
              fontWeight: subjectsOpen ? 700 : 600,
              color: subjectsOpen ? colors.ink : colors.inkMuted,
              letterSpacing: -0.2,
            }}
          />
          {subjectsOpen ? (
            <ExpandLessIcon sx={{ color: colors.accentOrange, fontSize: 28 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: colors.inkMuted, fontSize: 28 }} />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse
        id={subjectsPanelId}
        in={subjectsOpen}
        timeout="auto"
        unmountOnExit
        role="region"
        aria-labelledby={subjectsToggleId}
      >
        <Box component="div" sx={{ mt: 0.25 }}>
          {groups.map((group, groupIndex) => (
            <Box
              key={`group-${groupIndex}`}
              component="section"
              aria-label={group.title}
              sx={{ mb: 1.25 }}
            >
              <Typography
                variant="caption"
                component="h3"
                sx={{
                  px: 2.5,
                  py: 0.5,
                  fontWeight: 800,
                  fontSize: '0.6875rem',
                  color: colors.accentOrange,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  display: 'block',
                }}
              >
                {group.title}
              </Typography>
              {'description' in group && group.description && (
                <Typography
                  variant="caption"
                  component="p"
                  sx={{
                    px: 2.5,
                    pt: 0,
                    pb: 0.25,
                    fontSize: '0.75rem',
                    color: colors.inkMuted,
                    display: 'block',
                  }}
                >
                  {group.description}
                </Typography>
              )}
              <List
                component="div"
                disablePadding
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '2px 6px',
                  mt: 0.25,
                }}
              >
                {group.items.map(
                  (
                    levelItem: { label: string; path: string },
                    itemIndex: number
                  ) => (
                    <ListItem
                      key={`item-${groupIndex}-${itemIndex}`}
                      disablePadding
                      sx={{ minHeight: 0 }}
                    >
                      <ListItemButton
                        component={Link}
                        to={levelItem.path}
                        onClick={onClose}
                        sx={{
                          borderRadius: radii.sm,
                          minHeight: 40,
                          py: 0.75,
                          pl: 2.5,
                          pr: 1.5,
                          outline: '2px solid transparent',
                          outlineOffset: 2,
                          '&:focus-visible': {
                            outline: `3px solid ${colors.accentCobalt}`,
                            outlineOffset: 2,
                          },
                          '&:active': {
                            backgroundColor: colors.backgroundAlt,
                            transform: 'scale(0.98)',
                          },
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <ListItemText
                          primary={levelItem.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: colors.ink,
                            letterSpacing: -0.1,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                )}
              </List>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default LandingMobileNav;
